/**
 * FILE: extension-send.service.ts
 * CHỨC NĂNG: Giao tiếp với Vpage Chrome Extension qua window.postMessage để gửi tin nhắn
 * Facebook/Instagram khi không dùng được API trực tiếp (quá 24h, gửi file/media, reply cụ thể).
 *
 * Flow:
 * 1. ChannelAdapter.executeSend() kiểm tra shouldSendViaExtension()
 * 2. Nếu cần → gọi sendViaExtension() → tạo Promise, postMessage đến extension
 * 3. Extension xử lý (upload FB, gửi tin FB Messenger/IG)
 * 4. Extension gửi kết quả về qua window message event
 * 5. Service resolve/reject Promise tương ứng → queue tiếp tục
 */

import {inject, Injectable, isDevMode, NgZone} from '@angular/core';
import {firstValueFrom, Subject} from 'rxjs';
import {IChatMessage} from '../models/message.model';
import {CHANNEL} from '@shared/models';
import {CONVERSATION_TYPE} from '../models/conversation.model';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {ChatbotScriptApiService} from '@shared/services/api/chatbot/chatbot-script.service';
import {AuthService} from '@core/authentication/auth.service';
import {ConversationInteractionService} from './conversation-interaction.service';
import {
  EXT_MSG,
  EXT_ORIGIN,
  FB_RETRYABLE_ERROR_CODES,
  GENDER,
  generateFallbackId,
  MSG_STATUS_STRING,
  MSG_TYPE_STRING,
  PLATFORM,
  SEND_CONFIG,
  SEND_ERROR_MESSAGES,
  SEND_PHOTO_MODE,
  STORAGE_KEY,
} from '../models/conversation-constants.model';
import {LoggerService} from '@shared/services/global/logger.service';
import {isImageUrl} from '@shared/utils/file-type.util';

/** Trạng thái quét extension của một page */
interface IExtensionScanState {
  oldestTime: number | null;
  isComplete: boolean;
}

/** Dữ liệu tối thiểu để extension tìm Facebook global user ID từ một thread. */
export interface IFacebookGlobalUserIdRequest {
  pageId: string;
  threadId: string;
  recipientId: string;
  /** `IConversation.updatedTime` dùng epoch-second. */
  updatedTime?: number;
}

/** Dữ liệu để extension tìm global user ID của khách Instagram. */
export interface IInstagramGlobalUserIdRequest {
  pageId: string;
  recipientId: string;
  username?: string;
  /** Quét lại khi avatar Meta CDN đã hết hạn. */
  forceRescan?: boolean;
}

/** Task extension đang chờ response */
interface IPendingExtensionTask {
  taskId: string;
  conversationId: string;
  timestamp: number;
  resolve: (result: IChatMessage | null) => void;
  reject: (error: any) => void;
  timerId?: ReturnType<typeof setTimeout>;
}

/** Lỗi do Extension (hoặc socket dự phòng) trả về: bubble lỗi kèm link Hướng dẫn sửa lỗi Extension (FE13). */
export class ExtensionSendError extends Error {}

/**
 * Tin được trích dẫn khi trả lời qua extension (FE13 truyền nguyên object tin). Extension tải lùi
 * lịch sử thread (40 tin/lượt, tối đa 20 lượt) và khớp `createdTime` (giây) với timestamp của Facebook.
 */
export interface IExtensionReplyMessage {
  id: string;
  message: string;
  createdTime: number;
}

/** Attachment đã upload lên Facebook; `type` (image/file/video/audio) để extension chọn image_ids/file_ids. */
export interface IFbUploadedAttachment {
  attachment_id: string;
  type?: string;
}

/** Payload gửi qua window.postMessage đến extension */
export interface IExtensionPostMessagePayload {
  type: string;
  convId: string;
  /** ID attachment (ảnh), hoặc {attachment_id, type} để extension gửi đúng loại file/video (FE13). */
  files: Array<string | IFbUploadedAttachment>;
  from: string;
  message: string;
  photoUrls: string[];
  messageReply: IExtensionReplyMessage | '';
  threadId: string;
  globalUserId: string;
  taskId: string;
  pageId: string;
  customerId: string;
  platform: string;
  igPageId: string | null;
  username: string | null;
}

/** Params cho extension send */
export interface IExtensionSendParams {
  text: string;
  channel: number;
  type: number;
  fanpageId: string;
  conversationId: string;
  recipientId: string;
  fbUserId?: string;
  threadId?: string;
  messageReply?: IExtensionReplyMessage;
  fanpageUserName?: string;
  attachmentUrl?: string[];
  /** Local files cần upload lên cloud trước */
  blobs?: Array<{ blob: Blob; fileName: string }>;
  /** File đã có sẵn trên cloud */
  files?: Array<{ url: string; fileName: string }>;
  /** ID tạm của tin nhắn */
  tempMsgId: string;
  /** Có phải trả lời extension (24h rule) không */
  isReplyExtension?: boolean;
  /** updatedTime epoch_seconds của hội thoại — dùng cho isConversationTooOld */
  conversationUpdatedTime?: number;
  /** Partner reply check */
  isPartnerReply?: boolean;
}

@Injectable({providedIn: 'root'})
export class ExtensionSendService {
  private readonly api = inject(ConversationApiService);
  private readonly pageApi = inject(ChatbotScriptApiService);
  private readonly authService = inject(AuthService);
  private readonly ngZone = inject(NgZone);
  private readonly interactionService = inject(ConversationInteractionService);
  private readonly logger = inject(LoggerService);

  /** Extension có tồn tại không (detect từ message NHANH_EXTENSION_VERSION) */
  public isExisted = false;
  /** Version extension */
  public version = '';

  /** Trạng thái quét UID của extension theo pageId */
  private readonly extensionScanning = new Map<string, IExtensionScanState>();

  /** Map pending tasks: taskId → promise handlers */
  private readonly pendingTasks = new Map<string, IPendingExtensionTask>();

  /**
   * Yêu cầu tra UID phát sinh trước khi extension kịp trả version. V1 vẫn
   * postMessage trực tiếp ở thời điểm này; V2 từng return sớm theo isExisted
   * nên phải F5/chọn lại hội thoại mới quét được.
   */
  private readonly pendingFacebookGlobalUserIdRequests = new Map<string, IFacebookGlobalUserIdRequest>();
  /** Instagram lookup cũng có thể chạy trước event EXTENSION_VERSION. */
  private readonly pendingInstagramGlobalUserIdRequests = new Map<string, IInstagramGlobalUserIdRequest>();

  /**
   * Tập hợp conversationId đang có extension task chạy.
   * Dùng để tránh double-process webhook từ FB khi nhận socket message
   * (giống FE13 tasksExtension[]).
   */
  private readonly activeExtensionConversations = new Set<string>();

  /** Timeout mặc định chờ extension response */
  private readonly EXTENSION_TIMEOUT = SEND_CONFIG.EXTENSION_TIMEOUT;

  /** Send photo mode từ localStorage: 1=batch, 2=individual */
  private sendPhotoModes: string = SEND_PHOTO_MODE.BATCH;

  /** Subject phát sự kiện extension version loaded */
  private readonly _extensionReady$ = new Subject<void>();
  public readonly extensionReady$ = this._extensionReady$.asObservable();

  /** Subject phát sự kiện extension message cần component xử lý (customer info, avatar…) */
  public readonly onExtensionEvent = new Subject<{ type: string; data: any }>();

  constructor() {
    this.loadSendPhotoModes();
    this.listenToExtensionMessages();
    this.logger.log('[EXT] ExtensionSendService initialized. isDevMode:', isDevMode());

    // Lắng nghe extensionTaskCheck từ socket → nếu conv đang có task extension, skip message
    this.interactionService.eventBus$.subscribe(event => {
      if (event.type === 'extensionTaskCheck') {
        const payload = event.data as { conversationId: string; message: any };
        if (payload && this.isTaskExtensionActive(payload.conversationId)) {
          // Đánh dấu tin nhắn này đến từ extension → component filter
          this.onExtensionEvent.next({type: 'extensionTaskActive', data: payload});
        }
      }
    });
  }

  /** Gửi tín hiệu kiểm tra extension — gọi khi component init */
  public checkExtension(): void {
    this.logger.log('[EXT] checkExtension() called — sending CHECK_VERSION postMessage');
    if (typeof window !== 'undefined') {
      window.postMessage({type: EXT_MSG.CHECK_VERSION, from: EXT_ORIGIN.WEB});
      this.logger.log('[EXT] postMessage sent:', {type: EXT_MSG.CHECK_VERSION});
    } else {
      this.logger.warn('[EXT] window is undefined (SSR), cannot send postMessage');
    }
  }

  // ─── API công khai ───────────────────────────────────────────────────────

  /**
   * Kiểm tra có nên gửi qua extension không.
   * Extension MANDATORY cho:
   * - Inbox type
   * - Không phải partner reply
   * - Và 1 trong các điều kiện:
   * a) isReplyExtension (24h rule)
   * b) Có attachmentUrl (ảnh/file từ thư viện)
   * c) Có blob (file local)
   * d) Có messageReply và là Facebook
   */
  public shouldSendViaExtension(params: IExtensionSendParams): boolean {
    if (!this.isExisted) return false;
    return (
      params.type === CONVERSATION_TYPE.INBOX
      && !params.isPartnerReply
      && (
        params.isReplyExtension
        || (!!params.attachmentUrl?.length)
        || (!!params.blobs?.length)
        || (!!params.messageReply && params.channel === CHANNEL.FACEBOOK)
      )
    );
  }

  /**
   * Gửi tin nhắn qua extension.
   * Upload attachments trước (nếu có), sau đó postMessage đến extension.
   * Trả về Promise resolve khi extension phản hồi hoặc timeout.
   */
  public sendViaExtension(params: IExtensionSendParams): Promise<IChatMessage | null> {
    return new Promise<IChatMessage | null>((resolve, reject) => {
      // Kiểm tra blocking condition: hội thoại quá cũ, không quét được UID
      if (this.isConversationTooOld(params)) {
        reject(new Error(SEND_ERROR_MESSAGES.EXT_TOO_OLD));
        return;
      }
      // Không có mốc giây của tin gốc thì extension không thể tìm ra tin để trích dẫn.
      if (params.messageReply && !params.messageReply.createdTime) {
        reject(new Error(SEND_ERROR_MESSAGES.EXT_REPLY_NOT_FOUND));
        return;
      }

      const tempMsgId = params.tempMsgId;

      // Đăng ký pending task
      const task: IPendingExtensionTask = {
        taskId: tempMsgId,
        conversationId: params.conversationId,
        timestamp: Math.floor(Date.now() / 1000),
        resolve,
        reject,
        timerId: setTimeout(() => {
          this.pendingTasks.delete(tempMsgId);
          reject(new Error(SEND_ERROR_MESSAGES.EXT_TIMEOUT));
        }, this.EXTENSION_TIMEOUT),
      };
      this.pendingTasks.set(tempMsgId, task);

      // Bắt đầu flow gửi
      this.executeExtensionSend(params).catch(err => {
        this.cleanupPendingTask(tempMsgId);
        reject(err);
      });
    });
  }

  /**
   * Gửi lại tin nhắn qua socket khi extension báo lỗi có thể retry.
   * FE1: shouldRetrySocketOnError + sendMessage via MQTT
   */
  public async retryViaSocket(
    pageId: string,
    globalUserId: string,
    message: { text: string | null; files: string[] | null; fbReplyMsg: any },
    platform: string
  ): Promise<boolean> {
    // Socket retry chỉ hỗ trợ Facebook
    if (platform === PLATFORM.INSTAGRAM) return false;

    try {
      // Gọi API gửi tin qua BE (BE sẽ gửi socket)
      const res = await firstValueFrom(this.api.sendMessage({
        type: CONVERSATION_TYPE.INBOX,
        fanpageId: pageId,
        channel: CHANNEL.FACEBOOK,
        text: message.text || '',
        fbReplyMsg: message.fbReplyMsg,
        attachmentUrl: message.files,
      }));
      return res?.code === 1;
    } catch {
      return false;
    }
  }

  /** Lưu send photo mode từ localStorage */
  public loadSendPhotoModes(): void {
    try {
      this.sendPhotoModes = localStorage.getItem(STORAGE_KEY.SEND_PHOTO_MODES) ?? SEND_PHOTO_MODE.BATCH;
    } catch {
      this.sendPhotoModes = SEND_PHOTO_MODE.BATCH;
    }
  }

  /**
   * Lưu task extension (để tránh double-process webhook từ FB).
   * BE dùng danh sách này để biết tin nào do extension gửi, không nhận webhook.
   */
  public isTaskExtensionActive(conversationId: string): boolean {
    const now = Math.floor(Date.now() / 1000);
    for (const task of this.pendingTasks.values()) {
      if (task.conversationId === conversationId && (now - task.timestamp) < 60) {
        return true;
      }
    }
    return false;
  }

  /** Kiểm tra xem lỗi extension có retry được qua socket không */
  public shouldRetrySocketOnError(errorMessage: string): boolean {
    if (!errorMessage) return false;
    return FB_RETRYABLE_ERROR_CODES.some(code => errorMessage.includes(code));
  }

  /**
   * Lấy pageParentId cho Instagram (page FB liên kết với IG).
   * FE1: authService.getPageParentId()
   */
  public getPageParentId(pageId: string): string | null {
    const pages = this.authService.getPages(CHANNEL.INSTAGRAM);
    const page = pages.find(p => p.id === pageId || (p as any).igPageId === pageId);
    return (page as any)?.pageParentId ?? null;
  }

  // ─── Trạng thái quét UID của Extension ──────────────────────────────────

  /** Cập nhật trạng thái scan từ extension message (NHANH_CACHE_MAPPING_DATA) */
  public updateScanState(pageId: string, state: Partial<IExtensionScanState>): void {
    const current = this.extensionScanning.get(pageId) ?? {oldestTime: null, isComplete: false};
    if (state.oldestTime !== undefined) current.oldestTime = state.oldestTime;
    if (state.isComplete !== undefined) current.isComplete = state.isComplete;
    this.extensionScanning.set(pageId, current);
  }

  /** Lấy trạng thái scan của page */
  public getScanState(pageId: string): IExtensionScanState {
    return this.extensionScanning.get(pageId) ?? {oldestTime: null, isComplete: false};
  }

  /**
   * Yêu cầu extension tra Facebook global user ID cho một inbox thread.
   *
   * Chỉ quét khi extension chưa quét hết page, chưa có mốc cũ nhất, hoặc
   * hội thoại mới hơn mốc extension đã quét. Đây là cùng điều kiện của FE v1;
   * tránh gửi lệnh quét lặp lại cho các thread nằm ngoài phạm vi scan.
   */
  public requestFacebookGlobalUserId(params: IFacebookGlobalUserIdRequest): boolean {
    if (!params.pageId || !params.threadId || !params.recipientId || typeof window === 'undefined') {
      return false;
    }

    const scanState = this.getScanState(params.pageId);
    const conversationUpdatedAtMs = (params.updatedTime ?? 0) * 1000;
    const shouldScan = !scanState.isComplete
      || !scanState.oldestTime
      || conversationUpdatedAtMs > scanState.oldestTime;

    if (!shouldScan) {
      return false;
    }

    const customerId = `${params.pageId}_${params.recipientId}`;
    if (!this.isExisted) {
      this.pendingFacebookGlobalUserIdRequests.set(customerId, params);
      this.checkExtension();
      return true;
    }

    this.postFacebookGlobalUserIdRequest(params);
    return true;
  }

  private postFacebookGlobalUserIdRequest(params: IFacebookGlobalUserIdRequest): void {
    const customerId = `${params.pageId}_${params.recipientId}`;
    window.postMessage({
      type: EXT_MSG.GET_GLOBAL_USER_ID,
      from: EXT_ORIGIN.WEB,
      pageId: params.pageId,
      threadId: params.threadId,
      customerId,
    });
  }

  private flushPendingFacebookGlobalUserIdRequests(): void {
    if (!this.isExisted || typeof window === 'undefined') return;

    for (const [customerId, params] of this.pendingFacebookGlobalUserIdRequests) {
      this.pendingFacebookGlobalUserIdRequests.delete(customerId);
      this.postFacebookGlobalUserIdRequest(params);
    }
  }

  private flushPendingInstagramGlobalUserIdRequests(): void {
    if (!this.isExisted || typeof window === 'undefined') return;

    for (const [customerId, params] of this.pendingInstagramGlobalUserIdRequests) {
      this.pendingInstagramGlobalUserIdRequests.delete(customerId);
      void this.requestInstagramGlobalUserId(params);
    }
  }

  /**
   * Instagram không có Facebook threadId. Extension tra theo username trên
   * Facebook Page liên kết, đồng thời trả avatar/globalUserId để lưu customer.
   * Khác với Facebook inbox, v1 không giới hạn request này theo scan session.
   */
  public async requestInstagramGlobalUserId(params: IInstagramGlobalUserIdRequest): Promise<boolean> {
    if (!params.pageId || !params.recipientId || typeof window === 'undefined') {
      return false;
    }

    const customerId = `${params.pageId}_${params.recipientId}`;
    if (!this.isExisted) {
      this.pendingInstagramGlobalUserIdRequests.set(customerId, params);
      this.checkExtension();
      return true;
    }

    const pageParentId = await this.resolveInstagramPageParentId(params.pageId);
    this.postInstagramGlobalUserIdRequest(params, pageParentId);
    return true;
  }

  private postInstagramGlobalUserIdRequest(
    params: IInstagramGlobalUserIdRequest,
    pageParentId: string | null,
  ): void {
    window.postMessage({
      type: EXT_MSG.GET_GLOBAL_USER_ID,
      from: EXT_ORIGIN.WEB,
      pageId: pageParentId || params.pageId,
      customerId: `${params.pageId}_${params.recipientId}`,
      username: params.username || '',
      platform: PLATFORM.INSTAGRAM,
      igPageId: params.pageId,
      forceRescan: !!params.forceRescan,
    });
  }

  /** FE13 gọi getPageInfo khi auth chưa có pageParentId trước lúc gửi extension request. */
  private async resolveInstagramPageParentId(pageId: string): Promise<string | null> {
    const cached = this.getPageParentId(pageId);
    if (cached) return cached;

    try {
      const response = await firstValueFrom(this.pageApi.getPageInfo({id: pageId}));
      const data = (response as any)?.data ?? response;
      const item = data?.item
        ?? data?.items?.find?.((page: any) => String(page?.id) === String(pageId))
        ?? data?.[pageId]
        ?? data;
      const parentId = item?.parentPageId ?? item?.pageParentId ?? null;
      const normalized = parentId != null && String(parentId).trim()
        ? String(parentId).trim()
        : null;
      this.authService.setPageParentId(pageId, normalized);
      return normalized;
    } catch {
      return null;
    }
  }

  /** Yêu cầu extension lấy thêm giới tính/ngày sinh cho Facebook customer. */
  public requestFacebookUserInfo(customerId: string, fbUserId: string): boolean {
    if (!this.isExisted || !customerId || !fbUserId || typeof window === 'undefined') return false;

    window.postMessage({
      type: EXT_MSG.GET_FB_USER_INFO,
      from: EXT_ORIGIN.WEB,
      customerId,
      fuid: fbUserId,
    });
    return true;
  }

  // ─── Logic gửi tin qua Extension (private) ──────────────────────────────

  /**
   * Kiểm tra blocking: hội thoại không có fbUserId, có threadId, và scan đã hoàn thành
   * nhưng oldestTime > lastUpdateAt → không thể quét lại UID.
   */
  private isConversationTooOld(params: IExtensionSendParams): boolean {
    if (params.channel !== CHANNEL.FACEBOOK) return false;
    if (params.fbUserId) return false;
    if (!params.threadId) return false;

    const scanState = this.extensionScanning.get(params.fanpageId);
    if (!scanState?.isComplete || !scanState.oldestTime) return false;

    // `scanSession.oldestTime` do extension trả về là epoch-millisecond,
    // còn IConversation.updatedTime là epoch-second.
    const updatedTimeMs = (params.conversationUpdatedTime || 0) * 1000;
    return scanState.oldestTime > updatedTimeMs;
  }

  /**
   * Execute toàn bộ extension send flow:
   * 1. Upload attachments (URL) lên FB Graph via extension
   * 2. Upload blobs lên cloud → lấy URL → upload lên FB Graph
   * 3. Gửi text hoặc text+attachments qua extension
   */
  private async executeExtensionSend(params: IExtensionSendParams): Promise<void> {
    const {attachmentUrl, blobs} = params;

    // Chặn Instagram gửi file/ảnh qua extension
    if (params.channel === CHANNEL.INSTAGRAM && (attachmentUrl?.length || blobs?.length)) {
      throw new Error(SEND_ERROR_MESSAGES.EXT_IG_FILE_BLOCKED);
    }

    // Chặn gửi file khi isReplyExtension
    if (params.isReplyExtension && params.blobs?.length) {
      throw new Error(SEND_ERROR_MESSAGES.EXT_FILE_BLOCKED);
    }

    // Chuẩn bị attachmentUrls từ attachmentUrls (FE1 format)
    let allAttachmentUrls = attachmentUrl ? [...attachmentUrl] : [];

    // Upload blobs lên cloud trước
    if (blobs?.length) {
      const cloudUrls = await this.uploadBlobsToCloud(params.fanpageId, blobs);
      allAttachmentUrls.push(...cloudUrls);
    }

    // Upload attachments lên FB Graph (nếu có)
    let fbAttachments: IFbUploadedAttachment[] = [];
    if (allAttachmentUrls.length) {
      fbAttachments = await this.uploadAttachmentsToFb(params.fanpageId, allAttachmentUrls);
    }

    // Gửi tin qua extension
    await this.postMessageToExtension(params, fbAttachments, allAttachmentUrls);
  }

  /**
   * Upload blob files lên cloud server → trả về URLs.
   * FE1: uploadBlobs() → db.upload2cloud({ fanpageId, action: 'no-save', file, fileName })
   * Sử dụng postFormDataRequest (tự động thêm businessId) thay vì upload2cloud
   * để tránh lộn xộn về tham số businessId/fanpageId.
   */
  private async uploadBlobsToCloud(
    fanpageId: string,
    blobs: Array<{ blob: Blob; fileName: string }>
  ): Promise<string[]> {
    const uploadTasks = blobs.map(f => {
      const formData = new FormData();
      formData.append('fanpageId', fanpageId);
      formData.append('action', 'no-save');
      formData.append('file', f.blob, f.fileName);
      formData.append('fileName', f.fileName);
      return firstValueFrom(
        this.api.postFormDataRequest<any>('/business/attachment/upload2cloud', formData)
      );
    });

    const results = await Promise.all(uploadTasks);
    const urls: string[] = [];

    for (const res of results) {
      if (res?.file?.url) {
        urls.push(res.file.url);
      } else {
        throw new Error(SEND_ERROR_MESSAGES.UPLOAD_FAILED);
      }
    }

    return urls;
  }

  /**
   * Upload danh sách URLs lên Facebook Graph API (qua extension hoặc API).
   * FE1: uploadAttachments() → db.uploadAttachmentFb()
   * - Có file/video (không phải ảnh): upload từng URL kèm returnType như FE13 để giữ `type`,
   *   extension mới gửi đúng file_ids/video_ids thay vì coi tất cả là ảnh.
   * - Chỉ có ảnh: theo chế độ gửi ảnh — gộp batch hoặc từng ảnh.
   * Thiếu attachment nào thì báo lỗi, không gửi album thiếu.
   */
  private async uploadAttachmentsToFb(fanpageId: string, urls: string[]): Promise<IFbUploadedAttachment[]> {
    if (!urls.length) return [];

    if (urls.some(url => !isImageUrl(url)) || this.sendPhotoModes === SEND_PHOTO_MODE.INDIVIDUAL) {
      const results = await Promise.all(urls.map(url =>
        firstValueFrom(this.api.uploadAttachmentFb({url, fanpageId, returnType: true}))
      ));
      const attachments = results.map(res => this.toFbAttachment(res?.faceAttachmentId));
      if (attachments.some(attachment => !attachment)) throw new Error(SEND_ERROR_MESSAGES.FB_UPLOAD_FAILED);
      return attachments as IFbUploadedAttachment[];
    }

    // Batch mode: upload tất cả ảnh cùng lúc
    const res = await firstValueFrom(this.api.uploadAttachmentFb({urls, fanpageId, returnType: true}));
    const attachments = (Array.isArray(res?.faceAttachmentIds) ? res.faceAttachmentIds : [res?.faceAttachmentId])
      .map((item: unknown) => this.toFbAttachment(item))
      .filter((item: IFbUploadedAttachment | null): item is IFbUploadedAttachment => !!item);
    if (attachments.length !== urls.length) throw new Error(SEND_ERROR_MESSAGES.FB_UPLOAD_FAILED);
    return attachments;
  }

  /** BE trả attachment_id dạng chuỗi (không returnType) hoặc {attachment_id, type}. */
  private toFbAttachment(value: unknown): IFbUploadedAttachment | null {
    if (typeof value === 'string') return value ? {attachment_id: value} : null;
    const item = value as { attachment_id?: unknown; type?: unknown } | null | undefined;
    if (!item?.attachment_id) return null;
    return {
      attachment_id: String(item.attachment_id),
      ...(typeof item.type === 'string' && item.type ? {type: item.type} : {}),
    };
  }

  /**
   * Gửi postMessage đến extension với nội dung tin nhắn.
   * Hỗ trợ cả Facebook và Instagram (với pageParentId).
   */
  private async postMessageToExtension(
    params: IExtensionSendParams,
    fbAttachments: IFbUploadedAttachment[],
    photoUrls: string[]
  ): Promise<void> {
    const isInstagram = params.channel === CHANNEL.INSTAGRAM;
    const pageParentId = isInstagram ? this.getPageParentId(params.fanpageId) : null;

    const messagePayload: IExtensionPostMessagePayload = {
      type: EXT_MSG.REPLY_INBOX_PHOTO,
      convId: params.conversationId,
      // Không có type thì extension coi là ảnh (image_ids): file/video phải kèm type như FE13.
      files: fbAttachments.map(attachment => attachment.type ? attachment : attachment.attachment_id),
      from: EXT_ORIGIN.WEB,
      message: params.text || '',
      photoUrls,
      messageReply: params.messageReply ?? '',
      threadId: params.threadId ?? '',
      globalUserId: params.fbUserId ?? '',
      taskId: params.tempMsgId,
      pageId: pageParentId || params.fanpageId,
      customerId: `${params.fanpageId}_${params.recipientId}`,
      platform: isInstagram ? PLATFORM.INSTAGRAM : PLATFORM.FACEBOOK,
      igPageId: isInstagram ? params.fanpageId : null,
      username: isInstagram ? (params.fanpageUserName ?? '') : null,
    };

    // Trong môi trường browser an toàn
    if (typeof window !== 'undefined') {
      window.postMessage(messagePayload);
    }
  }

  // ─── Xử lý message từ Extension ─────────────────────────────────────────

  /**
   * Lắng nghe message từ extension.
   * Filter: only messages from "NHANH_EXTENSION" source.
   */
  private listenToExtensionMessages(): void {
    if (typeof window === 'undefined') {
      this.logger.warn('[EXT] window is undefined, cannot listen to extension messages');
      return;
    }

    this.logger.log('[EXT] Setting up window message listener for extension...');
    window.addEventListener('message', (event: MessageEvent) => {
      // Chỉ xử lý message từ extension
      if (event.data?.from !== EXT_ORIGIN.EXTENSION) return;

      this.logger.log('[EXT] Received message from extension:', event.data.type, event.data);

      // Chạy trong NgZone để Angular change detection hoạt động
      this.ngZone.run(() => {
        this.handleExtensionMessage(event);
      });
    });
  }

  private handleExtensionMessage(event: MessageEvent): void {
    const data = event.data;

    this.logger.labeled('EXT', 'handleExtensionMessage → type:', data.type);

    switch (data.type) {
      case EXT_MSG.EXTENSION_LOADED:
        this.logger.log('[EXT] Extension loaded → requesting version check');
        // Extension vừa load → kiểm tra version
        window.postMessage({type: EXT_MSG.CHECK_VERSION, from: EXT_ORIGIN.WEB});
        break;

      case EXT_MSG.EXTENSION_VERSION:
        this.logger.log('[EXT] ✅ Extension detected! Version:', data.version);
        this.isExisted = true;
        this.version = data.version ?? '';
        this._extensionReady$.next();
        // FE13: Gửi danh sách page FB để extension pre-initialize
        this.preinitializePages();
        this.flushPendingFacebookGlobalUserIdRequests();
        this.flushPendingInstagramGlobalUserIdRequests();
        break;

      case EXT_MSG.REPLY_SUCCESS:
        if (typeof data.convId === 'string' && data.convId.includes('msg_')) return;
        this.handleExtensionSuccess(data);
        break;

      case EXT_MSG.REPLY_FAILURE:
        if (data?.convId?.includes('msg_')) return;
        this.handleExtensionFailure(data);
        break;

      case EXT_MSG.WS_CONTEXT_SUCCESS:
        this.handleBusinessWsContextSuccess(data);
        break;

      case EXT_MSG.WS_CONTEXT_FAILURE:
        this.handleBusinessWsContextFailure(data);
        break;

      case EXT_MSG.CACHE_MAPPING_DATA:
        this.handleCacheMappingData(data);
        break;

      case EXT_MSG.GLOBAL_USER_ID_SUCCESS:
        this.handleGlobalUserIdSuccess(data);
        break;

      case EXT_MSG.FB_USER_INFO_SUCCESS:
        this.handleFbUserInfoSuccess(data);
        break;

      case EXT_MSG.ADD_REACTION_SUCCESS:
        this.handleAddReactionSuccess(data);
        break;

      default:
        this.logger.warn('[EXT] Unknown message type:', data.type, data);
        break;
    }
  }

  /**
   * Xử lý extension send thành công.
   * Resolve pending task với server message.
   */
  private handleExtensionSuccess(data: any): void {
    const pendingTask = this.findPendingTask(data.taskId, data.convId);
    if (!pendingTask) return;

    this.cleanupPendingTask(pendingTask.taskId);

    // Clear các timer
    if (pendingTask.timerId) clearTimeout(pendingTask.timerId);

    // Trả về kết quả thành công
    // [MSG-SEND-001] Fix GAP-65: Ưu tiên msgId/messageId thực từ Facebook, fallback taskId
    const result: IChatMessage = {
      id: data.msgId || data.messageId || data.taskId || generateFallbackId('ext'),
      conversationId: data.convId,
      sender: {id: '', name: '', isPage: true},
      content: data.message || '',
      type: MSG_TYPE_STRING.TEXT,
      attachments: data.attachments || [],
      status: MSG_STATUS_STRING.SENT,
      timestamps: {createdAt: data.createdTime || Date.now(), sentAt: Date.now()},
      metaData: this.buildGlobalUserIdMeta(data),
    };
    pendingTask.resolve(result);
  }

  /** UID khách Extension đã dùng để gửi — ChatCore lưu lại cho khách (FE13 handleMessageExtensionSuccess). */
  private buildGlobalUserIdMeta(data: any): { globalUserId: string } | undefined {
    return data?.globalUserId ? {globalUserId: String(data.globalUserId)} : undefined;
  }

  /**
   * Xử lý extension send thất bại.
   * Kiểm tra nếu lỗi có thể retry qua socket → retry, nếu không → reject.
   */
  private handleExtensionFailure(data: any): void {
    const pendingTask = this.findPendingTask(data.taskId, data.convId);
    if (!pendingTask) return;

    this.cleanupPendingTask(pendingTask.taskId);
    if (pendingTask.timerId) clearTimeout(pendingTask.timerId);

    // Kiểm tra nếu có thể retry qua socket
    const errorCode = data?.errorCode;
    const errorMsg = data?.error;
    const hasMessage = data?.message;
    const platform = data?.platform;

    if ((errorCode === 500 || this.shouldRetrySocketOnError(errorMsg)) && hasMessage && platform !== 'instagram') {
      // Retry qua socket — xử lý bất đồng bộ, không reject luôn
      this.retryViaSocket(
        data.pageId,
        data.globalUserId,
        {
          text: data.message?.text ?? null,
          files: data.message?.files?.length ? data.message.files.map((f: any) => f.attachment_id) : null,
          fbReplyMsg: data.message?.replyMessageId ? {message_id: data.message.replyMessageId} : null,
        },
        platform
      ).then(success => {
        if (success) {
          const result: IChatMessage = {
            id: data.taskId || generateFallbackId('ext_retry'),
            conversationId: data.convId,
            sender: {id: '', name: '', isPage: true},
            content: data.message?.text || '',
            type: MSG_TYPE_STRING.TEXT,
            attachments: [],
            status: MSG_STATUS_STRING.SENT,
            timestamps: {createdAt: Date.now(), sentAt: Date.now()},
            metaData: this.buildGlobalUserIdMeta(data),
          };
          pendingTask.resolve(result);
        } else {
          pendingTask.reject(new ExtensionSendError(errorMsg || 'Lỗi gửi tin qua Extension và Socket, vui lòng thử lại sau'));
        }
      }).catch(() => {
        pendingTask.reject(new ExtensionSendError(errorMsg || 'Lỗi gửi tin qua Extension và Socket, vui lòng thử lại sau'));
      });
    } else {
      pendingTask.reject(new ExtensionSendError(errorMsg || 'Lỗi gửi tin qua Extension, vui lòng thử lại sau'));
    }
  }

  /**
   * Xử lý NHANH_GET_BUSINESS_WS_CONTEXT_SUCCESS.
   * Extension trả về deviceId + currentFBUserData → gửi lại qua socket.
   */
  private handleBusinessWsContextSuccess(data: any): void {
    const pendingTask = this.findPendingTask(data.taskId);
    if (!pendingTask) return;

    const wsContext = data?.wsContext;
    if (!wsContext?.deviceId || !wsContext?.currentFBUserData) {
      this.cleanupPendingTask(pendingTask.taskId);
      pendingTask.reject(new ExtensionSendError('Không lấy được thông tin WebSocket từ extension.'));
      return;
    }

    // Retry qua socket với deviceId
    this.retryViaSocket(
      data.pageId,
      data.globalUserId,
      data.message,
      PLATFORM.FACEBOOK
    ).then(success => {
      this.cleanupPendingTask(pendingTask.taskId);
      if (success) {
        const result: IChatMessage = {
          id: data.taskId || generateFallbackId('ext_ws'),
          conversationId: pendingTask.conversationId,
          sender: {id: '', name: '', isPage: true},
          content: data.message?.text || '',
          type: MSG_TYPE_STRING.TEXT,
          attachments: [],
          status: MSG_STATUS_STRING.SENT,
          timestamps: {createdAt: Date.now(), sentAt: Date.now()},
          metaData: this.buildGlobalUserIdMeta(data),
        };
        pendingTask.resolve(result);
      } else {
        pendingTask.reject(new ExtensionSendError('Gửi tin qua socket thất bại.'));
      }
    }).catch(() => {
      this.cleanupPendingTask(pendingTask.taskId);
      pendingTask.reject(new ExtensionSendError('Gửi tin qua socket thất bại.'));
    });
  }

  private handleBusinessWsContextFailure(data: any): void {
    const pendingTask = this.findPendingTask(data.taskId);
    if (!pendingTask) return;
    this.cleanupPendingTask(pendingTask.taskId);
    pendingTask.reject(new ExtensionSendError('Không thể kết nối WebSocket Business.'));
  }

  /**
   * Xử lý cache mapping data từ extension (NHANH_CACHE_MAPPING_DATA).
   * Cập nhật scan session và thread meta.
   */
  private handleCacheMappingData(data: any): void {
    // Cập nhật scan session
    if (data?.scanSession) {
      const scanSession = data.scanSession;
      if (scanSession?.pageId) {
        this.updateScanState(scanSession.pageId, {
          oldestTime: scanSession.oldestTime ?? null,
          isComplete: scanSession.isComplete ?? false,
        });
      }
      // Gọi API lưu scan session
      this.api.updateScanSessionExtension({scanSession}).subscribe({
        error: () => {
        },
      });
    }

    // Cập nhật thread meta
    if (data?.mappingData?.length) {
      this.api.updateThreadMetaExtension({
        mappingData: JSON.stringify(data.mappingData),
      }).subscribe({
        error: () => {
        },
      });

      // Mapping thường được extension trả theo batch khi quét page. Forward
      // lên ChatCore để hội thoại đang mở dùng globalId ngay, không phải đợi
      // mở lại hội thoại cho BE tra mapping vừa lưu.
      this.onExtensionEvent.next({
        type: EXT_MSG.CACHE_MAPPING_DATA,
        data,
      });
    }
  }

  // ─── PREINITIALIZE pages ───────────────────────────────────────────────

  /**
   * Gửi danh sách page FB để extension pre-initialize.
   * FE13: conversation.component.ts:2470-2478
   * Gọi khi nhận được EXTENSION_VERSION → extension đã sẵn sàng.
   */
  private preinitializePages(): void {
    const fbPages = this.authService.getPages(CHANNEL.FACEBOOK);
    if (fbPages.length > 0) {
      const pageIds = fbPages.map(p => p.id);
      window.postMessage({
        type: EXT_MSG.PREINITIALIZE_PAGES,
        pageIds,
        from: EXT_ORIGIN.WEB,
      });
    }
  }

  // ─── Xử lý FB_USER_INFO_SUCCESS ────────────────────────────────────────

  /**
   * Xử lý NHANH_GET_FB_USER_INFO_SUCCESS.
   * Extension trả về gender / birthDate / birthYear của customer → cập nhật DB.
   * FE13: conversation.component.ts:2482-2502
   */
  private handleFbUserInfoSuccess(data: any): void {
    const userInfo = data?.userInfo;
    if (!data?.customerId || !userInfo) return;

    const params: Record<string, any> = {id: data.customerId};
    const customerPatch: Record<string, number> = {};

    if (userInfo?.gender) {
      const genderKey = (userInfo.gender as string).toUpperCase();
      const customerGender = (GENDER as any)[genderKey] ?? GENDER.OTHER;
      params['gender'] = customerGender;
      customerPatch['gender'] = customerGender;
    }

    if (userInfo?.birthDate || userInfo?.birthYear) {
      const birthday = this.standardizeDate(userInfo?.birthDate, userInfo?.birthYear);
      if (birthday) {
        params['birthday'] = birthday;
        customerPatch['birthday'] = birthday;
      }
    }

    if (Object.keys(customerPatch).length) {
      this.api.updateCustomer(params).subscribe({
        error: () => {
        }
      });
    }

    // Forward cho component layer cập nhật UI state
    this.onExtensionEvent.next({
      type: EXT_MSG.FB_USER_INFO_SUCCESS,
      data: {...data, customerPatch},
    });
  }

  // ─── Xử lý GLOBAL_USER_ID_SUCCESS ──────────────────────────────────────

  /**
   * Xử lý NHANH_GET_GLOBAL_USER_ID_SUCCESS.
   * Extension trả về globalUserId, profilePicUrl → cập nhật customer.
   * FE13: conversation.component.ts:2504-2545
   */
  private handleGlobalUserIdSuccess(data: any): void {
    if (!data?.customerId || !data?.globalUserId) return;

    this.pendingFacebookGlobalUserIdRequests.delete(String(data.customerId));

    // Extension có thể chỉ trả ID mà không có avatar. Không gửi avatar=null
    // vì sẽ xoá avatar đang có trong DB.
    const customerPatch: Record<string, unknown> = {
      id: data.customerId,
      fbUserId: data.globalUserId,
    };
    if (data.profilePicUrl) customerPatch['avatar'] = data.profilePicUrl;

    this.api.updateCustomer(customerPatch).subscribe({
      error: () => {
      }
    });

    // Forward cho component layer cập nhật conversation state, avatar DOM, loadLinkFacebook…
    this.onExtensionEvent.next({type: EXT_MSG.GLOBAL_USER_ID_SUCCESS, data});
  }

  // ─── Xử lý ADD_REACTION_SUCCESS ────────────────────────────────────────

  /**
   * Xử lý NHANH_ADD_REACTION_MESSAGE_SUCCESS.
   * Extension gửi reaction thành công → lưu emoji vào DB.
   * FE13: conversation.component.ts:2593-2606
   */
  private handleAddReactionSuccess(data: any): void {
    const msg = data?.message ?? null;
    const emoji = data?.emoji ?? null;
    if (!msg || !emoji) return;

    this.api.updateEmojiMessage({
      msgId: msg.id,
      pageId: msg.fanpageId,
      emoji,
    }).subscribe({
      error: () => {
      }
    });
  }

  // ─── Utility: chuẩn hóa ngày tháng từ FB ───────────────────────────────

  /**
   * Chuẩn hóa ngày tháng từ FB user info (birthDate, birthYear).
   * FE13: conversation.component.ts:2420-2457
   */
  private standardizeDate(dateStr?: string, yearStr?: string): number | null {
    const monthMap: Record<string, string> = {
      January: '01', February: '02', March: '03', April: '04',
      May: '05', June: '06', July: '07', August: '08',
      September: '09', October: '10', November: '11', December: '12',
    };

    let day: string | null = null;
    let month: string | null = null;
    const year = yearStr?.trim() ?? null;

    if (dateStr) {
      const normalizedDate = dateStr.trim();
      const parts = normalizedDate.split(/\s+/);
      if (normalizedDate.includes('tháng') && parts.length >= 3) {
        // Format tiếng Việt extension có thể trả: "4 tháng 11".
        day = parts[0]?.padStart(2, '0') ?? null;
        month = parts[2]?.padStart(2, '0') ?? null;
      } else if (parts.length >= 2) {
        // Format tiếng Anh: "January 15" hoặc "January 15, 1990".
        month = monthMap[parts[0]] ?? null;
        day = parts[1].replace(',', '').padStart(2, '0');
      } else if (parts.length === 1 && normalizedDate.includes('/')) {
        // "dd/MM" format
        const dm = normalizedDate.split('/');
        day = dm[0]?.padStart(2, '0');
        month = dm[1]?.padStart(2, '0');
      }
    }

    if (day && month && year) {
      return this.toUnixBirthday(year, month, day);
    }
    if (month && year) {
      return this.toUnixBirthday(year, month, '01');
    }
    if (year) {
      return this.toUnixBirthday(year, '01', '01');
    }
    return null;
  }

  /** Customer API dùng epoch-second cho birthday, giống form cập nhật khách hàng. */
  private toUnixBirthday(year: string, month: string, day: string): number | null {
    const value = new Date(`${year}-${month}-${day}T00:00:00`).getTime();
    return Number.isNaN(value) ? null : Math.floor(value / 1000);
  }

  // ─── Hàm tiện ích ───────────────────────────────────────────────────────

  /**
   * Tìm pending task theo taskId hoặc conversationId.
   */
  private findPendingTask(taskId: string, convId?: string): IPendingExtensionTask | undefined {
    if (taskId && this.pendingTasks.has(taskId)) {
      return this.pendingTasks.get(taskId);
    }
    if (convId) {
      for (const task of this.pendingTasks.values()) {
        if (task.conversationId === convId) return task;
      }
    }
    return undefined;
  }

  /** Cleanup pending task */
  private cleanupPendingTask(taskId: string): void {
    const task = this.pendingTasks.get(taskId);
    if (task?.timerId) clearTimeout(task.timerId);
    this.pendingTasks.delete(taskId);
  }

  /** Cleanup tất cả pending tasks */
  public cleanupAll(): void {
    for (const task of this.pendingTasks.values()) {
      if (task.timerId) clearTimeout(task.timerId);
    }
    this.pendingTasks.clear();
  }
}
